import { useState } from 'react';
import { Link } from 'react-router-dom';

import { FUNNEL_STAGE_LABELS, type DialogDto, type FunnelStage } from '../../api/types';
import { cn } from '../../lib/cn';
import { Avatar } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { ChannelBadge } from './ChannelBadge';
import { DialogRowMenu } from './DialogRowMenu';
import { HighlightText } from './HighlightText';
import { SlaBadge } from './SlaBadge';
import { StateBadge } from './StateBadge';

export function contactName(dialog: DialogDto): string {
  const { firstName, lastName } = dialog.contact;
  const name = [firstName, lastName].filter(Boolean).join(' ');
  return name || `VK ID ${dialog.contact.vkUserId}`;
}

function stageLabel(stage: string): string {
  return FUNNEL_STAGE_LABELS[stage as FunnelStage] ?? stage;
}

function timeLabel(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

export interface DialogListItemProps {
  dialog: DialogDto;
  /** Route to navigate to on click — defaults to the standalone dialog page, the two-pane inbox passes /inbox/:id. */
  to?: string;
  active?: boolean;
  /** Substring to highlight in the name/VK id (§U1 search). */
  searchQuery?: string;
}

export function DialogListItem({ dialog, to, active = false, searchQuery = '' }: DialogListItemProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const name = contactName(dialog);
  const vkId = String(dialog.contact.vkUserId);
  const time = timeLabel(dialog.lastMessage?.createdAt ?? dialog.updatedAt);

  return (
    <Link
      to={to ?? `/dialogs/${dialog.id}`}
      data-testid={`dialog-row-${dialog.id}`}
      onContextMenu={(e) => {
        e.preventDefault();
        setMenuOpen(true);
      }}
      className={cn(
        'flex gap-3 border-b border-border px-3 py-3 hover:bg-muted',
        active && 'bg-muted',
      )}
    >
      <Avatar name={name} photoUrl={dialog.contact.photoUrl} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <ChannelBadge type={dialog.channelType} />
            <span className="truncate font-medium">
              <HighlightText text={name} query={searchQuery} />
            </span>
            {dialog.awaitingHuman && (
              <Badge tone="warning" aria-label="ждёт ответа" title="Ждёт ответа человека">
                ждёт ответа
              </Badge>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <span className="text-xs text-muted-fg">{time}</span>
            <DialogRowMenu dialog={dialog} open={menuOpen} onOpenChange={setMenuOpen} />
          </div>
        </div>
        <div className="truncate text-xs text-muted-fg">
          VK ID <span>{<HighlightText text={vkId} query={searchQuery} />}</span>
        </div>
        {dialog.lastMessage?.text && (
          <p className="truncate text-sm text-muted-fg">{dialog.lastMessage.text}</p>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <StateBadge state={dialog.state} />
          <SlaBadge sla={dialog.slaStatus} />
          <Badge tone="info" data-testid="stage-badge">
            {stageLabel(dialog.contact.funnelStage)}
          </Badge>
        </div>
      </div>
    </Link>
  );
}
